import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import flattenDeep from 'lodash-es/flattenDeep';
import cuid from 'cuid';
import {
  useGetSignedUploadUrlMutation,
  useCreateStripePaymentIntentMutation,
  PaymentMedium,
  StripePaymentMethodType,
  useCreateFormSubmissionMutation,
  FormSubmissionCreateInput,
  ChannelViewChannelFragment,
  UserWhereUniqueInput,
  MessageType,
  ChannelMessageAttachmentType,
  ChannelMessageAttachmentEntityType,
  ChannelMessageAttachmentCreateWithoutChannelMessageInput,
  ChannelMessageAttachmentUploadStatus,
  useTriggerMessageEventMutation,
  SqsQueueNames,
  CreateChannelMessageDocument,
  CreateChannelMessageMutationVariables,
  CreateChannelMessageMutation,
} from 'shared/types/graphql';
import { createUniqueId } from '../utils';
import { IAttachmentOption } from 'pages/Conversations/interfaces/IAttachmentOption';
import { IPreparedVideoAttachment } from 'pages/Conversations/interfaces/IPreparedVideoAttachment';
import { UPDATE_CHANNEL, UPDATE_CHANNEL_MESSAGE_ATTACHMENT } from '../graphql/mutations';
import useClinicUser from 'shared/hooks/useClinicUser';
import { isNotNullOrUndefined } from 'shared/utils';
import { IMentionedUser } from '../components/ChannelView/MessageComposer/Mentions/MentionsList';
import { useParams } from 'react-router-dom';
import { MessageEvents } from 'shared/enums/MessageEvents';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useFetchGraphQLQuery } from './useFetchGraphQLQuery';

interface ISendMessageProps {
  text: string;
  attachments: IAttachmentOption[];
  messageType: MessageType;
  sendAsChatMessage: boolean;
  mentionedUsers: IMentionedUser[];
}

interface IUseSendMessageProps {
  channel?: ChannelViewChannelFragment | null;
  onError: (error: Error) => void;
  onSuccess?: (data: CreateChannelMessageMutation) => void;
}
export const useSendMessage = ({
  channel,
  onError,
  onSuccess,
}: IUseSendMessageProps): { sendMessage?: (props: ISendMessageProps) => Promise<string | undefined | false> } => {
  const { clinicUser, currentClinicId } = useClinicUser();
  const [updateChannel] = useMutation(UPDATE_CHANNEL);
  const [createChannelMessage] = useFetchGraphQLQuery<
    CreateChannelMessageMutation,
    CreateChannelMessageMutationVariables
  >({
    query: CreateChannelMessageDocument,
    onSuccess,
  });

  const [triggerMessageEvent] = useTriggerMessageEventMutation();
  const [updateChannelMessageAttachment] = useMutation(UPDATE_CHANNEL_MESSAGE_ATTACHMENT);
  const [getSignedUploadUrl] = useGetSignedUploadUrlMutation();
  const { channelId } = useParams<{ channelId: string }>();
  const { trackEvent } = useTracking();

  const uploadFile = useCallback(
    async (attachment: IAttachmentOption): Promise<IAttachmentOption | null> => {
      if (!attachment.file || !channel) return null;

      const file = attachment.file;

      try {
        if (!currentClinicId) {
          throw new Error('No current clinic id');
        }
        const { data } = await getSignedUploadUrl({
          variables: {
            data: {
              key: `${cuid()}_${file.name}`,
              contentType: file.type,
            },
          },
        });
        if (!data?.getSignedUploadUrl.uploadUrl) {
          throw new Error('Upload Failed');
        }
        await fetch(data.getSignedUploadUrl.uploadUrl, {
          method: 'Put',
          body: file,
          headers: { 'Content-Type': file.type },
        });

        let attachmentType = ChannelMessageAttachmentType.File;

        if (file.type.includes('image')) {
          attachmentType = ChannelMessageAttachmentType.Image;
        } else if (file.type.includes('video')) {
          attachmentType = ChannelMessageAttachmentType.Video;
        }

        return {
          id: createUniqueId(),
          value: {
            id: cuid(),
            attachmentType,
            filename: file.name,
            url: data.getSignedUploadUrl.finalUrl,
            uploadStatus: ChannelMessageAttachmentUploadStatus.Success,
          },
        };
      } catch (e) {
        throw new Error('The attached file was unable to be uploaded. Please try again.');
      }
    },
    [channel, currentClinicId, getSignedUploadUrl],
  );

  const [createFormSubmission] = useCreateFormSubmissionMutation();

  const createFormSubmissions = useCallback(
    async (attachment: IAttachmentOption): Promise<IAttachmentOption[] | null> => {
      if (!channel?.clinicId) {
        throw new Error('Clinic not found for form submission.');
      }

      // TODO: Change data model to accept multiple pet parents
      const clinicPetParentId = channel.channelMembers?.filter((m) => !!m.clinicPetParent)[0].clinicPetParent?.id || '';
      const data: FormSubmissionCreateInput = {
        formTemplate: { connect: { id: attachment.value.entityId } },
        clinic: { connect: { id: channel.clinicId } },
        clinicPetParent: {
          connect: {
            id: clinicPetParentId,
          },
        },
        draftContent: attachment.formTemplateContent,
      };

      let formSubmissions = [];

      if (!attachment.clinicPetIds?.length) {
        formSubmissions = [
          await createFormSubmission({
            variables: {
              data,
            },
          }),
        ];
      } else {
        // Create a separate FormSubmission for each pet linked to this form request attachment
        formSubmissions = await Promise.all(
          attachment.clinicPetIds.map((clinicPetId) =>
            createFormSubmission({
              variables: {
                data: {
                  ...data,
                  clinicPet: {
                    connect: { id: clinicPetId },
                  },
                },
              },
            }),
          ),
        );
      }
      trackEvent(ConversationEvents.FormSent);
      return formSubmissions.map((s) => ({
        id: createUniqueId(),
        value: {
          id: cuid(),
          attachmentType: ChannelMessageAttachmentType.FormRequest,
          entityType: ChannelMessageAttachmentEntityType.Form,
          entityId: s.data?.createOneFormSubmission?.id,
          uploadStatus: ChannelMessageAttachmentUploadStatus.Success,
        },
      }));
    },
    [channel, createFormSubmission, trackEvent],
  );

  const [createStripePaymentIntent] = useCreateStripePaymentIntentMutation();

  const createInvoice = useCallback(
    async (attachment: IAttachmentOption): Promise<IAttachmentOption | null> => {
      const amount = Number(attachment.invoiceAmount?.replace('.', ''));
      const clientServiceFeePercent = attachment?.clientServiceFeePercent;
      if (isNaN(amount)) return null;

      let filename, url;

      /**
       * Try-catch file upload and creating payment request separately, so we know which one
       * fails, if any, and we can show the appropriate error message
       */
      try {
        if (attachment.file) {
          const { value } = (await uploadFile(attachment)) as IAttachmentOption;
          filename = value.filename;
          url = value.url;
        }
      } catch (e) {
        console.error(e);
        throw new Error('An error occurred uploading your file. Please try again.');
      }

      try {
        const variables = {
          data: {
            clientServiceFeePercent,
            amount,
            paymentMethodType: StripePaymentMethodType.CardNotPresent,
          },
          channelId: channel?.id,
          paymentMedium: PaymentMedium.StripeOnline,
          invoiceIds: attachment.invoiceIds || null,
          paymentRelationship: attachment.paymentRelationship || null,
        };

        const stripePaymentIntent = await createStripePaymentIntent({
          variables,
        });

        if (!stripePaymentIntent?.data?.createStripePaymentIntent2) return null;

        trackEvent(ConversationEvents.InvoiceSent);
        return {
          id: createUniqueId(),
          value: {
            id: cuid(),
            attachmentType: ChannelMessageAttachmentType.Invoice,
            entityType: ChannelMessageAttachmentEntityType.StripePaymentIntent,
            entityId: stripePaymentIntent.data.createStripePaymentIntent2.id,
            filename,
            url,
            uploadStatus: ChannelMessageAttachmentUploadStatus.Success,
          },
        };
      } catch (e) {
        console.error(e);
        throw new Error('Could not create payment request. Please try again.');
      }
    },
    [uploadFile, channel?.id, createStripePaymentIntent, trackEvent],
  );

  const prepareAttachments = useCallback(
    async (attachments: IAttachmentOption[]): Promise<IAttachmentOption[]> => {
      const preparedAttachments = await Promise.all(
        attachments.map(async (attachment) => {
          switch (attachment.value.attachmentType) {
            case ChannelMessageAttachmentType.Image:
            case ChannelMessageAttachmentType.File:
              return uploadFile(attachment);
            case ChannelMessageAttachmentType.FormRequest:
              return createFormSubmissions(attachment);
            case ChannelMessageAttachmentType.Invoice:
              return createInvoice(attachment);
            default:
              return attachment;
          }
        }),
      );

      return flattenDeep<IAttachmentOption>(preparedAttachments.filter((a) => !!a) as IAttachmentOption[]);
    },
    [uploadFile, createFormSubmissions, createInvoice],
  );

  /**
   * In the case of a network error or some other issue, lets make sure the
   * error is reported in the attachment uploadStatus
   */
  const handleChannelMessageAttachmentUploadFailure = useCallback(
    (channelMessageAttachmentId: string) => {
      try {
        updateChannelMessageAttachment({
          variables: {
            where: {
              id: channelMessageAttachmentId,
            },
            data: {
              uploadStatus: ChannelMessageAttachmentUploadStatus.Error,
            },
          },
        });
      } catch (e) {
        console.error(e);
      }
    },
    [updateChannelMessageAttachment],
  );

  /**
   * Upload IPreparedVideoAttachment[] for processing
   */
  const uploadVideos = useCallback(
    async (preparedVideoAttachmentOptions: IPreparedVideoAttachment[]): Promise<void> => {
      await Promise.all(
        preparedVideoAttachmentOptions.map((preparedVideoAttachmentOption) => {
          const { channelMessageAttachmentId, file, uploadUrl } = preparedVideoAttachmentOption;

          if (!uploadUrl) {
            handleChannelMessageAttachmentUploadFailure(channelMessageAttachmentId);
            return null;
          }

          return fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type,
            },
          });
        }),
      );

      await Promise.all(
        preparedVideoAttachmentOptions.map((preparedVideoAttachmentOption) => {
          const { channelMessageAttachmentId, file } = preparedVideoAttachmentOption;
          return triggerMessageEvent({
            variables: {
              event: MessageEvents.CONVERT_CHANNEL_MESSAGE_ATTACHMENT_TO_MP4,
              queueName: SqsQueueNames.MediaJobs,
              data: {
                channelMessageAttachmentId,
                filename: file.name,
                contentType: file.type,
              },
              useSubscription: true,
            },
          });
        }),
      );
    },
    [triggerMessageEvent, handleChannelMessageAttachmentUploadFailure],
  );

  /**
   * Prepare IPreparedVideoAttachment from IAttachmentOption
   */
  const prepareVideoAttachment = useCallback(
    async (attachment: IAttachmentOption): Promise<IPreparedVideoAttachment | null> => {
      if (!attachment.file || !channel) return null;
      const channelMessageAttachmentId = cuid();
      const key = `conversations/${channel.id}/${channelMessageAttachmentId}`;
      const { data } = await getSignedUploadUrl({
        variables: {
          data: {
            key,
            contentType: attachment.file.type,
          },
        },
      });
      if (!data?.getSignedUploadUrl?.uploadUrl) {
        throw new Error(
          `Unable to generate signed upload URL for video attachment ${attachment.file.name}. Please try again.`,
        );
      }
      const { finalUrl, uploadUrl } = data.getSignedUploadUrl;
      return {
        file: attachment.file,
        channelMessageAttachmentId,
        uploadUrl,
        finalUrl,
        key,
        channelMessageAttachment: {
          id: channelMessageAttachmentId,
          attachmentType: ChannelMessageAttachmentType.Video,
          filename: attachment.file.name,
          url: data.getSignedUploadUrl.finalUrl,
          uploadStatus: ChannelMessageAttachmentUploadStatus.InProgress,
        },
      };
    },
    [channel, getSignedUploadUrl],
  );

  const sendMessage = useCallback(
    async ({
      text,
      messageType,
      attachments,
      sendAsChatMessage,
      mentionedUsers,
    }: ISendMessageProps): Promise<string | undefined | false> => {
      if (!channel || !clinicUser || (!channel.id && !channelId)) return false;
      try {
        const preparedAttachments = (
          await prepareAttachments(
            attachments.filter((a) => a.value.attachmentType !== ChannelMessageAttachmentType.Video),
          )
        ).map(({ value }) => value);

        const preparedVideoAttachments = await Promise.all(
          attachments
            .filter((a) => a.value.attachmentType === ChannelMessageAttachmentType.Video)
            .map((videoAttachments) => prepareVideoAttachment(videoAttachments)),
        );

        const channelMessageAttachmentsVideo = preparedVideoAttachments
          .map((pva) => pva?.channelMessageAttachment)
          .filter((cma) => !!cma) as ChannelMessageAttachmentCreateWithoutChannelMessageInput[];

        const author = channel.channelMembers?.find((m) => m.user?.id === clinicUser.id);
        if (!author) throw new Error('No author found for creating channel message');

        const channelMessage = await createChannelMessage({
          data: {
            body: text,
            attachments: { create: [...preparedAttachments, ...channelMessageAttachmentsVideo] },
            author: {
              connect: { id: author.id },
            },
            index: 1, // The actual value is calculated on the backend at message creation
            twilioMessageSid: '',
            channel: { connect: { id: channel.id || channelId } },
            messageType,
            forceReadReceipt: sendAsChatMessage,
            mentionedUsers: {
              connect: mentionedUsers.map((user): UserWhereUniqueInput => {
                return { id: user.id };
              }),
            },
          },
        });

        uploadVideos(preparedVideoAttachments.filter(isNotNullOrUndefined));
        // TODO: Ensure channel member consumed message index matches new message index (core-api)
        if (messageType === MessageType.Message)
          trackEvent(ConversationEvents.ChannelMessageSent, {
            sendAsChatMessage,
            channelType: channel.channelType || '',
          });
        else trackEvent(ConversationEvents.ChannelNoteSent);
        if (!!preparedAttachments.length) {
          trackEvent(ConversationEvents.ChannelAttachmentSent, {
            quantity: preparedAttachments.length,
            channelType: channel.channelType || '',
          });
        }

        const isUserAssignee = channel.assignees?.some(({ id }) => id === clinicUser.id);

        if (!isUserAssignee) {
          const assigneeUserIds = channel.assignees?.map(({ id }) => ({ id }))?.concat({ id: clinicUser.id });
          updateChannel({
            variables: {
              where: { id: channel.id },
              data: { friendlyName: channel.friendlyName, assignees: { connect: assigneeUserIds } },
            },
          });
        }
        return channelMessage.data?.createChannelMessage2.id;
      } catch (error) {
        if (error instanceof Error) {
          onError(error);
        }
        return false;
      }
    },
    [
      channel,
      clinicUser,
      channelId,
      prepareAttachments,
      createChannelMessage,
      uploadVideos,
      trackEvent,
      prepareVideoAttachment,
      updateChannel,
      onError,
    ],
  );

  return { sendMessage };
};
